
import { Link } from "react-router-dom";
import AuthForm from "@/components/auth/AuthForm";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Info, Mail, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

const SignUp = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [signupAttempted, setSignupAttempted] = useState(false);
  const [showEmailConfirmationHelp, setShowEmailConfirmationHelp] = useState(false);
  const [registrationEmail, setRegistrationEmail] = useState<string | null>(null);

  // Redirect to dashboard if user is already logged in
  useEffect(() => {
    if (user) {
      console.log('User already logged in, redirecting to dashboard');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Log signup attempts for debugging
  useEffect(() => {
    return () => {
      if (signupAttempted) {
        console.log('SignUp component unmounted after signup attempt');
      }
    };
  }, [signupAttempted]);

  const handleSignupAttempt = (email?: string) => {
    setSignupAttempted(true);
    setShowEmailConfirmationHelp(true);
    if (email) {
      setRegistrationEmail(email);
    }
    console.log('Signup attempt registered in SignUp component', { email });
  };

  const handleResendVerification = async () => {
    if (!registrationEmail) {
      toast.error("No email address available for verification");
      return;
    }
    
    try {
      toast.info(`Attempting to resend verification to ${registrationEmail}`);
      
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: registrationEmail
      });
      
      if (error) {
        console.error('Error resending verification:', error);
        toast.error(`Failed to resend: ${error.message}`);
      } else {
        toast.success("Verification email sent again", {
          description: "Please check your inbox (and spam folder)"
        });
      }
    } catch (error: any) {
      console.error('Exception when resending verification:', error);
      toast.error(`Error: ${error.message}`);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      
      <div className="flex-1 flex items-center justify-center px-4 py-20">
        <div className="w-full max-w-md animate-fade-in">
          {showEmailConfirmationHelp && (
            <Alert className="mb-6" variant={registrationEmail ? "default" : "destructive"}>
              {registrationEmail ? (
                <>
                  <CheckCircle className="h-4 w-4" />
                  <AlertTitle>Check your email</AlertTitle>
                  <AlertDescription className="space-y-4">
                    <p>
                      We've sent a verification email to <strong>{registrationEmail}</strong>. 
                      Please check your inbox and spam folder.
                    </p>
                    <div className="flex items-center gap-2 pt-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={handleResendVerification}
                        className="flex items-center gap-1"
                      >
                        <Mail className="h-3 w-3" />
                        Resend verification email
                      </Button>
                    </div>
                  </AlertDescription>
                </>
              ) : (
                <>
                  <Info className="h-4 w-4" />
                  <AlertTitle>Check your email after signing up</AlertTitle>
                  <AlertDescription>
                    After you register, you may need to confirm your email address before logging in. 
                    Please check your inbox and spam folder for a confirmation link.
                  </AlertDescription>
                </>
              )}
            </Alert>
          )}

          <AuthForm type="sign-up" onSubmitAttempt={handleSignupAttempt} />
          
          <div className="mt-6 text-center text-sm text-muted-foreground">
            <p>
              Already have an account?{" "}
              <Link to="/sign-in" className="text-primary hover:underline">
                Sign in instead
              </Link>
            </p>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default SignUp;
